import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Avatar,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { fetchHubOwners } from '../../../store/hubOwner/actionCreators';
import { setFacilitiesBreadcrumbs } from '../../../store/facility/actionCreators';
import FacilityStatusIcon from '../../ModernFacility/components/FacilityStatusIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import OverviewTab from './components/OverviewTab';
import InsightsTab from './components/InsightsTab/InsightsTab';
import DetailsTab from './components/DetailsTab';
import MedicationTab from './components/MedicationTab';
import CareTeamTab from './components/CareTeamTab';
import {
  fetchProfilePicture,
  getCustomer,
} from '../../../store/customers/actionCreators';
import {
  GENDER_LABELS,
  INSIGHTS_VIEW_ROLES,
  PATIENT_EDIT_ROLES,
  SurveyType,
  UserRoles,
  VIDEO_CALL_PROVIDER_ROLE_IDS,
} from '../../../shared/constants';
import ScheduleTab from './components/ScheduleTab/ScheduleTab';
import { readPatientDetailsState } from './patientDetailsLink';
import PatientDeleteDialog from './components/PatientDeleteDialog';
import EditPatientDetailsModal from './components/EditPatientDetailsModal';
import {
  dimGrey,
  inactive,
  lightBlack,
  lightPurple,
  lightTeal,
  outlineSeparator,
} from '../../../mainTheme';
import { formatCityStateZip, getCurrentUserRole } from '../../../shared/utils';
import { requestOutgoingCall } from '../../../store/calls/actionCreators';

const tabStyles = {
  indicator: {
    backgroundColor: lightTeal,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    color: dimGrey,
    lineHeight: '20px',
    letterSpacing: '0.24px',
  },
  selected: {
    color: `${lightBlack} !important`,
  },
};

export interface TabComponentProps {
  ownerId: number;
  ownerName?: string;
  switchToMedicationTab?: () => void;
  facility_id: number;
  isFacilityAdmin: boolean;
  ownerEmail?: string;
  // Survey type the Insights tab opens on when the page was navigated to
  // with one (patientDetailsLink.ts).
  initialSurveyType?: SurveyType;
}

interface TabConfig {
  label: string;
  component: React.ComponentType<TabComponentProps>;
  // When set, the tab renders only for these roles; absent means every role
  // that can reach the page.
  visibleFor?: UserRoles[];
}

const tabConfig: TabConfig[] = [
  { label: 'Overview', component: OverviewTab },
  { label: 'Details', component: DetailsTab },
  { label: 'Schedule', component: ScheduleTab },
  { label: 'Medication', component: MedicationTab },
  { label: 'Care team', component: CareTeamTab },
  {
    label: 'Insights',
    component: InsightsTab,
    visibleFor: INSIGHTS_VIEW_ROLES,
  },
];

const PatientDetails = () => {
  const [isOwnersLoading, setIsOwnersLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  // Another page may ask for a tab (and survey type) to open on; see
  // patientDetailsLink.ts.
  const requested = readPatientDetailsState(useLocation().state);

  const { hubOwners } = useSelector((state: RootState) => state.hubOwner);
  const { profilePicture, profilePictureLoading, facilityCustomers } =
    useSelector((state: RootState) => state.customers);
  const hubOwner = hubOwners?.find((owner) => owner.customer_id === Number(id));
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );
  const activeCall = useSelector((state: RootState) => state.calls.activeCall);

  const currentRole = getCurrentUserRole(dashboardUserInfo);

  const isFacilityAdmin = currentRole === UserRoles.FACILITY_ADMIN;

  const canEditPatient = PATIENT_EDIT_ROLES.includes(currentRole);

  // Provider view: only provider/clinical roles may start a video call (OI-1564).
  const canStartVideoCall = VIDEO_CALL_PROVIDER_ROLE_IDS.includes(
    dashboardUserInfo?.facility_role_id ?? -1,
  );

  const ownerDetails = (facilityCustomers || []).find(
    (customer) => customer.customer_id === Number(id),
  );

  const visibleTabs = useMemo(
    () =>
      tabConfig.filter(
        (tab) => !tab.visibleFor || tab.visibleFor.includes(currentRole),
      ),
    [currentRole],
  );

  // Opens on the requested tab when the viewer can see it, else Overview.
  const [selectedTab, setSelectedTab] = useState(() =>
    Math.max(
      0,
      visibleTabs.findIndex((tab) => tab.label === requested.tab),
    ),
  );

  const medicationTabIndex = visibleTabs.findIndex(
    (tab) => tab.label === 'Medication',
  );

  const formattedAddress = [
    ownerDetails?.address1,
    ownerDetails?.address2,
    formatCityStateZip(
      ownerDetails?.city ?? '',
      ownerDetails?.state ?? '',
      ownerDetails?.zipcode ?? '',
    ),
  ]
    .filter(Boolean)
    .join(', ');
  const contextLine = [
    ...(hubOwner?.facility_id != null
      ? [
          hubOwner?.parent_facility_name || 'N/A',
          hubOwner?.facility_name || 'N/A',
        ]
      : []),
    formattedAddress,
  ]
    .filter(Boolean)
    .join(' · ');

  useEffect(() => {
    if (id) {
      dispatch(fetchProfilePicture(Number(id)));
      dispatch(getCustomer(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const loadOwners = async () => {
      if (!hubOwners?.length) {
        setIsOwnersLoading(true);
        try {
          await dispatch(fetchHubOwners());
        } finally {
          setIsOwnersLoading(false);
        }
      }
    };
    loadOwners();
  }, [dispatch, hubOwners?.length]);

  // This is the code to update the breadcrumbs of the Topbar
  useEffect(() => {
    const crumbs = [{ label: 'Patients', path: '/v2/patients' }];

    if (id) {
      const patient = hubOwners?.find(
        (owner) => owner.customer_id?.toString() === id,
      );
      if (patient) {
        crumbs.push({
          label:
            `${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
            'Patient',
          path: `/v2/patients/${id}`,
        });
      }
    }

    dispatch(setFacilitiesBreadcrumbs(crumbs));

    // Clear on unmount
    return () => {
      dispatch(setFacilitiesBreadcrumbs([]));
    };
  }, [dispatch, id, hubOwners]);

  if (isOwnersLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            navigate(-1);
          }}
          sx={{
            border: `1px solid ${inactive}`,
            borderRadius: '10px',
            width: '60px',
            height: '40px',
            '&:hover': {
              border: `1px solid ${inactive}`,
            },
            marginBottom: '24px',
          }}
        >
          Back
        </Button>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {}}
            sx={{
              height: '40px',
            }}
            disabled={
              !(
                (ownerDetails?.device_id || hubOwner?.device_id) &&
                (ownerDetails?.device_serial_number || hubOwner?.serial_number)
              )
            }
          >
            Send alert
          </Button>
          {canStartVideoCall && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                // Start an in-page v2 Janus call (OI-1564) — the global
                // VideoCallModalV2 + controller take over from here.
                const displayName =
                  ownerDetails?.name ||
                  `${hubOwner?.first_name ?? ''} ${hubOwner?.last_name ?? ''}`.trim() ||
                  id ||
                  'Patient';
                dispatch(requestOutgoingCall(Number(id), displayName));
              }}
              sx={{
                height: '40px',
              }}
              disabled={
                !!activeCall ||
                !(
                  (ownerDetails?.device_id || hubOwner?.device_id) &&
                  (ownerDetails?.device_serial_number ||
                    hubOwner?.serial_number)
                )
              }
            >
              Video call
            </Button>
          )}
        </Box>
      </Box>

      <Box p="12px" borderRadius="8px" border={`1px solid ${inactive}`}>
        <Box display="flex" gap={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {profilePictureLoading ? (
              <CircularProgress />
            ) : (
              <Avatar
                src={profilePicture ?? ''}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: '#555',
                }}
              >
                {hubOwner?.first_name?.charAt(0)}
              </Avatar>
            )}
          </Box>
          <Box display="flex" flexDirection="column" gap="8px" width="100%">
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" gap={2}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '22px',
                    lineHeight: '30px',
                    letterSpacing: '0.36px',
                    color: lightBlack,
                  }}
                >
                  {hubOwner
                    ? `${hubOwner.first_name || ''} ${hubOwner.last_name || ''}`.trim()
                    : 'N/A'}
                </Typography>
                <FacilityStatusIcon
                  status={
                    hubOwner?.med_status_level as 'ok' | 'warning' | 'critical'
                  }
                />
              </Box>
              <Box display="flex">
                {canEditPatient && (
                  <Tooltip title="Edit">
                    <Box
                      sx={{
                        display: 'flex',
                        width: 32,
                        height: 32,
                        p: '8px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px 0 0 8px',
                        border: `1px solid ${outlineSeparator}`,
                        borderRight: `1px solid ${outlineSeparator}`,
                      }}
                    >
                      <IconButton
                        aria-label="Edit patient"
                        onClick={() => setEditModalOpen(true)}
                        size="small"
                      >
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          size="sm"
                          style={{ width: 16, height: 16, flexShrink: 0 }}
                        />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <Box
                    sx={{
                      display: 'flex',
                      width: 32,
                      height: 32,
                      p: '8px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: canEditPatient ? '0 8px 8px 0' : '8px',
                      border: `1px solid ${outlineSeparator}`,
                      ...(canEditPatient && { borderLeft: 'none' }),
                    }}
                  >
                    <IconButton
                      aria-label="Delete patient"
                      onClick={() => setShowDeleteDialog(true)}
                      size="small"
                    >
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        style={{ width: 16, height: 16, flexShrink: 0 }}
                        size="sm"
                      />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            {contextLine && (
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.2px',
                  color: dimGrey,
                }}
              >
                {contextLine}
              </Typography>
            )}
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: dimGrey,
              }}
            >
              {hubOwner?.gender_id !== undefined &&
              GENDER_LABELS[hubOwner.gender_id]
                ? GENDER_LABELS[hubOwner.gender_id]
                : 'N/A'}{' '}
              · Age {hubOwner?.age || 'N/A'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: lightPurple,
              }}
            >
              {hubOwner?.email || 'N/A'} · {hubOwner?.phone || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTabs-indicator': tabStyles.indicator,
          '& .MuiTab-root': tabStyles.tab,
          '& .Mui-selected': tabStyles.selected,
          marginTop: '16px',
        }}
      >
        {visibleTabs.map(({ label }) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      <Box sx={{ borderBottom: 1, borderColor: inactive }} />
      <Box mt={2}>
        {visibleTabs[selectedTab]?.component &&
          React.createElement(visibleTabs[selectedTab].component, {
            ownerId: Number(id),
            ownerName: hubOwner
              ? `${hubOwner.first_name || ''} ${hubOwner.last_name || ''}`.trim()
              : undefined,
            facility_id: hubOwner?.facility_id || 0,
            switchToMedicationTab: () => setSelectedTab(medicationTabIndex),
            isFacilityAdmin,
            ownerEmail: hubOwner?.email,
            initialSurveyType: requested.surveyType,
          })}
      </Box>

      {showDeleteDialog && (
        <PatientDeleteDialog
          onClose={() => setShowDeleteDialog(false)}
          patientId={hubOwner?.customer_id || Number(id)}
          onSuccess={() => {
            setShowDeleteDialog(false);
            navigate('/v2/patients');
            dispatch(fetchHubOwners());
          }}
        />
      )}
      {editModalOpen && hubOwner && (
        <EditPatientDetailsModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          patient={{
            first_name: hubOwner.first_name || '',
            last_name: hubOwner.last_name || '',
            email: hubOwner.email || '',
            facility: hubOwner.parent_facility_name || '',
            location: hubOwner.facility_name || '',
            phone: hubOwner.phone || '',
            address1: ownerDetails?.address1 || '',
            address2: ownerDetails?.address2 || '',
            city: ownerDetails?.city || '',
            state: ownerDetails?.state || '',
            zipcode: ownerDetails?.zipcode || '',
            date_of_birth: hubOwner.date_of_birth || '',
            gender: GENDER_LABELS[hubOwner.gender_id] || '',
            // wing/room_number come from GET /customer/:id (ownerDetails);
            // the /hub_owners list item doesn't include them.
            wing: ownerDetails?.wing || hubOwner.wing || '',
            room_number:
              ownerDetails?.room_number || hubOwner.room_number || '',
          }}
          profilePicture={profilePicture}
          patientId={hubOwner.customer_id}
          isFacilityPatient={hubOwner.facility_id != null}
          onSuccess={async ({ edited, uploaded }) => {
            if (edited) {
              setIsOwnersLoading(true);
              try {
                await Promise.all([
                  dispatch(fetchHubOwners()),
                  dispatch(getCustomer(Number(id))),
                ]);
              } finally {
                setIsOwnersLoading(false);
              }
            }
            if (uploaded) {
              dispatch(fetchProfilePicture(Number(id)));
            }
          }}
        />
      )}
    </Box>
  );
};

export default PatientDetails;
